import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { adminService } from '../services/api';
import MainLayout from '../components/MainLayout';

const AdminAnnouncements: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuthStore();
  
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeLanguageTab, setActiveLanguageTab] = useState('en');

  const languages = ['en', 'es', 'zh', 'de', 'ru'];
  const languageLabels: Record<string, string> = {
    en: 'English',
    es: 'Español',
    zh: '中文',
    de: 'Deutsch',
    ru: 'Русский'
  };

  const [formData, setFormData] = useState({
    en: { title: '', content: '' },
    es: { title: '', content: '' },
    zh: { title: '', content: '' },
    de: { title: '', content: '' },
    ru: { title: '', content: '' }
  });

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/');
      return;
    }

    fetchAnnouncements();
  }, [isAuthenticated, isAdmin, navigate]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await adminService.getNews();
      // Group announcements by ID to get all language versions
      const grouped: Record<string, any> = {};
      (res.data || []).forEach((item: any) => {
        if (!grouped[item.id]) {
          grouped[item.id] = {};
        }
        grouped[item.id][item.language_code || 'en'] = item;
      });
      setAnnouncements(Object.values(grouped));
      setError('');
    } catch (err: any) {
      console.error('Error fetching announcements:', err);
      setError('Error loading announcements');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      en: { title: '', content: '' },
      es: { title: '', content: '' },
      zh: { title: '', content: '' },
      de: { title: '', content: '' },
      ru: { title: '', content: '' }
    });
    setActiveLanguageTab('en');
    setEditingId(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate that English is required
    if (!formData.en.title || !formData.en.content) {
      setError('English (title and content) is required');
      return;
    }

    try {
      if (editingId) {
        await adminService.updateNews(editingId, formData);
        setMessage('Announcement updated successfully');
      } else {
        await adminService.createNews(formData);
        setMessage('Announcement created successfully');
      }

      resetForm();
      setShowForm(false);
      fetchAnnouncements();
      setTimeout(() => setMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save announcement');
    }
  };

  const handleEdit = (announcementGroup: any) => {
    const newFormData = { ...formData };
    languages.forEach(lang => {
      if (announcementGroup[lang]) {
        newFormData[lang as keyof typeof newFormData] = {
          title: announcementGroup[lang].title,
          content: announcementGroup[lang].content
        };
      }
    });
    setFormData(newFormData);
    setEditingId(announcementGroup.en?.id || announcementGroup[languages[0]]?.id);
    setActiveLanguageTab('en');
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this announcement (all languages)?')) {
      try {
        await adminService.deleteNews(id);
        setMessage('Announcement deleted successfully');
        fetchAnnouncements();
        setTimeout(() => setMessage(''), 3000);
      } catch (err: any) {
        setError('Failed to delete announcement');
      }
    }
  };

  if (loading) {
    return <MainLayout><div className="max-w-6xl mx-auto px-4 py-8"><p className="text-center text-gray-600">Loading...</p></div></MainLayout>;
  }

  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Manage Announcements</h1>

      {error && <p className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</p>}
      {message && <p className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-4">{message}</p>}

      <section>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">Announcements</h2>
          <button onClick={() => {
            if (showForm) {
              resetForm();
            }
            setShowForm(!showForm);
          }} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
            {showForm ? 'Cancel' : 'New Announcement'}
          </button>
        </div>

        {showForm && (
          <form className="bg-white rounded-lg shadow-md p-6 mb-6" onSubmit={handleSubmit}>
            {/* Language Tabs */}
            <div className="flex border-b border-gray-300 mb-6">
              {languages.map(lang => (
                <button
                  key={lang}
                  type="button"
                  className={`px-4 py-2 font-semibold border-b-2 ${
                    activeLanguageTab === lang
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-600 hover:text-gray-800'
                  }`}
                  onClick={() => setActiveLanguageTab(lang)}
                >
                  {languageLabels[lang]}
                </button>
              ))}
            </div>

            {/* Language Content */}
            <div className="mb-6">
              <input
                type="text"
                placeholder="Title"
                value={formData[activeLanguageTab as keyof typeof formData].title}
                onChange={(e) => setFormData({
                  ...formData,
                  [activeLanguageTab]: { ...formData[activeLanguageTab as keyof typeof formData], title: e.target.value }
                })}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 mb-4"
              />
              <textarea
                placeholder="Content"
                value={formData[activeLanguageTab as keyof typeof formData].content}
                onChange={(e) => setFormData({
                  ...formData,
                  [activeLanguageTab]: { ...formData[activeLanguageTab as keyof typeof formData], content: e.target.value }
                })}
                rows={5}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              />
            </div>

            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {editingId ? 'Update Announcement (All Languages)' : 'Create Announcement (All Languages)'}
            </button>
          </form>
        )}

        {announcements.length > 0 ? (
          <div className="space-y-4">
            {announcements.map((annGroup) => {
              const firstLang = languages.find(lang => annGroup[lang]);
              const id = firstLang ? annGroup[firstLang].id : '';
              const title = firstLang ? annGroup[firstLang].title : 'N/A';
              const publishedAt = firstLang ? annGroup[firstLang].published_at : '';
              const author = firstLang ? annGroup[firstLang].author : '';
              
              return (
                <div key={id} className="bg-white rounded-lg shadow-md p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">Multi-language</span>
                  </div>
                  <p className="text-gray-700 mb-2">{annGroup[firstLang || 'en']?.content}</p>
                  {author && publishedAt && (
                    <small className="text-gray-600">By {author} on {new Date(publishedAt).toLocaleDateString()}</small>
                  )}
                  <div className="flex gap-2 mt-4">
                    <button onClick={() => handleEdit(annGroup)} className="px-3 py-1 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600">Edit</button>
                    <button onClick={() => handleDelete(id)} className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600">Delete</button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600">No announcements yet</p>
        )}
      </section>
      </div>
    </MainLayout>
  );
};

export default AdminAnnouncements;
